// hooks/useContactEnquiry.js
import { useState, useEffect } from 'react';
import { 
  database, 
  ref, 
  set, 
  push, 
  get 
} from '../firebase.config';

const MONTH_NAMES = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec"
];

export const useContactEnquiry = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [years, setYears] = useState([]);

  // Get current year and month
  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear().toString();
    const monthIndex = now.getMonth();
    const monthName = MONTH_NAMES[monthIndex];
    return { year, monthName, timestamp: now.toISOString() };
  };

  // Submit a new contact enquiry
  const submitContactEnquiry = async (formData) => {
    setIsLoading(true);
    
    const { year, monthName, timestamp } = getCurrentDate();
    
    const enquiryData = {
      ...formData,
      timestamp,
      year,
      month: monthName,
      monthIndex: MONTH_NAMES.indexOf(monthName),
      read: false,
      status: 'new',
      userAgent: navigator.userAgent,
    };

    try {
      // Add to year/month collection
      const yearMonthRef = ref(database, `contactEnquiries/${year}/${monthName}`);
      const newEnquiryRef = push(yearMonthRef);
      await set(newEnquiryRef, enquiryData);

      console.log('Contact enquiry submitted successfully');
      return { success: true, id: newEnquiryRef.key };
    } catch (error) {
      console.error('Error submitting contact enquiry:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Get all enquiries for a specific year and month
  const getEnquiriesByMonth = async (year, monthName) => {
    try {
      const monthRef = ref(database, `contactEnquiries/${year}/${monthName}`);
      const snapshot = await get(monthRef);
      
      if (snapshot.exists()) {
        const enquiries = [];
        snapshot.forEach((childSnapshot) => {
          enquiries.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        // Sort by timestamp (newest first)
        enquiries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        return enquiries;
      }
      return [];
    } catch (error) {
      console.error('Error fetching enquiries by month:', error);
      return [];
    }
  };

  // Get all enquiries for a specific year
  const getEnquiriesByYear = async (year) => {
    try {
      const yearRef = ref(database, `contactEnquiries/${year}`);
      const snapshot = await get(yearRef);
      
      if (snapshot.exists()) {
        const yearData = snapshot.val();
        const allEnquiries = [];
        
        Object.keys(yearData).forEach(month => {
          const monthData = yearData[month];
          Object.keys(monthData).forEach(enquiryId => {
            allEnquiries.push({
              id: enquiryId,
              ...monthData[enquiryId]
            });
          });
        });
        
        // Sort by timestamp (newest first)
        allEnquiries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        return allEnquiries;
      }
      return [];
    } catch (error) {
      console.error('Error fetching enquiries by year:', error);
      return [];
    }
  };

  // Get ALL enquiries from all years
  const getAllEnquiries = async (filters = {}) => {
    try {
      const allRef = ref(database, 'contactEnquiries');
      const snapshot = await get(allRef);
      
      if (snapshot.exists()) {
        const allYears = snapshot.val();
        const allEnquiries = [];
        
        // Loop through all years
        Object.keys(allYears).forEach(year => {
          const yearData = allYears[year];
          
          // Loop through all months in the year
          Object.keys(yearData).forEach(month => {
            const monthData = yearData[month];
            
            // Loop through all enquiries in the month
            Object.keys(monthData).forEach(enquiryId => {
              const enquiry = monthData[enquiryId];
              
              // Apply filters
              let shouldInclude = true;
              
              if (filters.year && year !== filters.year) shouldInclude = false;
              if (filters.month && month !== filters.month) shouldInclude = false;
              if (filters.status && enquiry.status !== filters.status) shouldInclude = false;
              if (filters.read !== undefined && enquiry.read !== filters.read) shouldInclude = false;
              
              if (shouldInclude) {
                allEnquiries.push({
                  id: enquiryId,
                  ...enquiry
                });
              }
            });
          });
        });
        
        // Sort by timestamp (newest first)
        allEnquiries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        return allEnquiries;
      }
      return [];
    } catch (error) {
      console.error('Error fetching all enquiries:', error);
      return [];
    }
  };

  // Get total count of ALL enquiries
  const getTotalEnquiryCount = async () => {
    try {
      const snapshot = await get(ref(database, 'contactEnquiries'));
      if (!snapshot.exists()) return 0;
      
      let total = 0;
      const allYears = snapshot.val();
      
      // Count all enquiries across all years and months
      Object.keys(allYears).forEach(year => {
        const yearData = allYears[year];
        Object.keys(yearData).forEach(month => {
          const monthData = yearData[month];
          total += Object.keys(monthData).length;
        });
      });
      
      return total;
    } catch (error) {
      console.error('Error getting total enquiry count:', error);
      return 0;
    }
  };

  // Get count by month for a specific year (for charts)
  const getMonthlyCountsForYear = async (year) => {
    try {
      const monthlyCounts = new Array(12).fill(0);
      const yearRef = ref(database, `contactEnquiries/${year}`);
      const snapshot = await get(yearRef);
      
      if (snapshot.exists()) {
        const yearData = snapshot.val();
        
        MONTH_NAMES.forEach((monthName, index) => {
          if (yearData[monthName]) {
            monthlyCounts[index] = Object.keys(yearData[monthName]).length;
          }
        });
      }
      
      return monthlyCounts;
    } catch (error) {
      console.error('Error getting monthly counts:', error);
      return new Array(12).fill(0);
    }
  };

  // Extract all available years
  const extractYears = async () => {
    try {
      const snapshot = await get(ref(database, 'contactEnquiries'));
      const yearList = snapshot.exists() 
        ? Object.keys(snapshot.val()).sort((a, b) => b - a)
        : [];
      setYears(yearList);
      return yearList;
    } catch (error) {
      console.error('Error extracting years:', error);
      setYears([]);
      return [];
    }
  };

  // Update enquiry status (mark as read, contacted, etc.)
  const updateEnquiryStatus = async (enquiryId, year, month, updates) => {
    try {
      if (!year || !month) {
        // If year/month not provided, find the enquiry first
        const allEnquiries = await getAllEnquiries();
        const enquiry = allEnquiries.find(e => e.id === enquiryId);
        
        if (!enquiry) {
          return { success: false, error: 'Enquiry not found' };
        }
        
        year = enquiry.year;
        month = enquiry.month;
      }
      
      // Get current enquiry data
      const enquiryRef = ref(database, `contactEnquiries/${year}/${month}/${enquiryId}`);
      const snapshot = await get(enquiryRef);
      
      if (snapshot.exists()) {
        const currentData = snapshot.val();
        const updatedData = {
          ...currentData,
          ...updates
        };
        
        await set(enquiryRef, updatedData);
        return { success: true };
      }
      
      return { success: false, error: 'Enquiry not found' };
    } catch (error) {
      console.error('Error updating enquiry status:', error);
      return { success: false, error: error.message };
    }
  };

  // Initialize - fetch years and total count
  useEffect(() => {
    const initialize = async () => {
      await extractYears();
      const total = await getTotalEnquiryCount();
      setTotalCount(total);
    };
    
    initialize();
  }, []);

  return {
    // State
    isLoading,
    totalCount,
    years,
    
    // Submit
    submitContactEnquiry,
    
    // Read operations
    getEnquiriesByMonth,
    getEnquiriesByYear,
    getAllEnquiries,
    getMonthlyCountsForYear,
    
    // Statistics
    getTotalEnquiryCount,
    
    // Management
    updateEnquiryStatus,
    extractYears,
    
    // Constants
    MONTH_NAMES,
  };
};